/* eslint-disable class-methods-use-this */

import { BaseUser, InitResult, LoginResult } from '@pro4-erp/core-web';
import { User } from './user';
import env from '@/env';

type ErrorResponse = {
  status: number;
  success: false;
  message: string;
  data: null;
};

type LoginResponseDto = {
  accessToken: string;
  user: User;
};

const validateTokenAsync = async ({ accessToken }: { accessToken: string }): Promise<boolean> => {
  try {
    await fetch(`${env.VITE_API_URL}/pb/s/api/auth/portal/token_validate`, {
      method: 'POST',
      headers: {
        Authenticate: `Bearer ${accessToken}`,
      },
    });

    return true;
  } catch {
    return false;
  }
};

const getUserAsync = async ({ accessToken }: { accessToken: string }): Promise<User | null> => {
  try {
    const fetchResponse = await fetch(`${env.VITE_API_URL}/pb/s/api/sqo-portal/users/me`, {
      headers: {
        Authenticate: `Bearer ${accessToken}`,
      },
    });

    if (!fetchResponse.ok) {
      return null;
    }

    const response: any = await fetchResponse.json();

    return response.data;
  } catch {
    return null;
  }
};

export class UserImplementation extends BaseUser<User> {
  static async updateToken(): Promise<LoginResult<User>> {
    const accessToken = localStorage.getItem('accessToken');

    try {
      const response = await fetch(`${env.VITE_API_URL}/pb/s/api/sqo-portal/auth/update-token`, {
        method: 'GET',
        headers: {
          Authenticate: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const responseData = (await response.json()) as ErrorResponse;
        return {
          success: false,
          message: responseData.message,
          data: null,
          goToTwoFactor: false,
        };
      }

      const responseData = (await response.json()) as LoginResponseDto;

      // TODO: Maybe also add rememberMe to params
      localStorage.setItem('accessToken', responseData.accessToken);

      return {
        success: true,
        message: 'Successfully logged in.',
        data: responseData.user,
        goToTwoFactor: false,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: `An unknown error occured: ${String(error)}`,
        data: null,
        goToTwoFactor: false,
      };
    }
  }

  async init(): Promise<InitResult<User>> {
    const accessToken = localStorage.getItem('accessToken');
    console.log(accessToken);
    if (!accessToken) {
      return {
        success: false,
        message: 'Access token not found in local storage.',
        data: null,
      };
    }

    try {
      const isTokenValid = await validateTokenAsync({ accessToken });

      if (!isTokenValid) {
        return {
          success: false,
          message: 'Access token is not valid.',
          data: null,
        };
      }

      const user = await getUserAsync({ accessToken });

      if (!user) {
        return {
          success: false,
          message: 'An unknown error occurred when trying to get user info.',
          data: null,
        };
      }

      return {
        success: true,
        message: 'Successfully got user from access token.',
        data: user,
      };
    } catch (error) {
      return {
        success: false,
        message: 'An unknown error occurred',
        data: null,
      };
    }
  }

  async login({
    identifier,
    password,
  }: {
    identifier: string;
    password: string;
  }): Promise<LoginResult<User>> {
    try {
      const response = await fetch(`${env.VITE_API_URL}/pb/s/api/sqo-portal/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: identifier,
          password,
        }),
      });

      if (!response.ok) {
        const responseData = (await response.json()) as ErrorResponse;
        return {
          success: false,
          message: responseData.message,
          data: null,
          goToTwoFactor: false,
        };
      }

      const responseData = (await response.json()) as LoginResponseDto;

      // TODO: Maybe also add rememberMe to params
      localStorage.setItem('accessToken', responseData.accessToken);

      return {
        success: true,
        message: 'Successfully logged in.',
        data: responseData.user,
        goToTwoFactor: false,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: `An unknown error occured: ${String(error)}`,
        data: null,
        goToTwoFactor: false,
      };
    }
  }

  logout(): any {
    localStorage.removeItem('accessToken');

    return {
      success: true,
      message: 'Successfully logged out.',
      data: null,
    };
  }
}
